import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiShield,
  FiZap,
  FiDroplet,
  FiWind,
  FiAward,
  FiTool,
} from 'react-icons/fi';
import {
  BsDroplet,
  BsRecycle,
  BsShieldCheck,
  BsLightning,
} from 'react-icons/bs';
import {
  GiChemicalDrop,
  GiFactory,
  GiOilDrum,
  GiWheat,
  GiMechanicalArm,
  GiCargoShip,
  GiMining,
  GiElectric,
  GiCarWheel,
  GiCrane,
  GiTank,
  GiRailway,
} from 'react-icons/gi';

const Products = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const coreProducts = [
    {
      name: 'RU DSA-6',
      type: 'Engine and Industrial Maintenance',
      tagline: 'De-Scaling Agent',
      icon: '⚙️',
      color: 'from-vp-blue-600 to-vp-blue-500',
      accentColor: 'from-blue-400 to-cyan-400',
      function: 'An Eco-Friendly, Bio-Degradable & Water Based solution specifically developed to dissolve and remove stubborn scales from parts in continuous contact with water.',
      keyBenefit: 'Cleans radiators and water jackets completely, without the need for demounting the engine, and enhances equipment life by up to 50% when used properly and continuously. It is a safe alternative to conventional methods that may use corrosive acids, such as caustic soda, and other hazardous chemicals.',
      idealFor: [
        'Compressors',
        'Oil Coolers',
        'Chillers',
        'Condensers',
        'Boilers',
        'Tubings',
        'Copper tubes',
        'GT coolers',
        'Heat Exchangers',
      ],
      path: '/products/ru-dsa-6',
    },
    {
      name: 'RU ORA-7',
      type: 'Heavy Industry Cleaning and Maintenance',
      tagline: '3-in-1 Industrial Solution',
      icon: '🛡️',
      color: 'from-orange-600 to-red-600',
      accentColor: 'from-orange-400 to-red-400',
      function: 'A high-performance, multi-purpose solution that acts as an Oil Remover, De-Ruster, and Carbon Remover in a single, safe, water-based product.',
      keyBenefit: 'Cleans any type of oil from any surface, removes rust without damaging the original metal, and is non-flammable, making it ideal for high-risk industrial environments.',
      idealFor: [
        'Heavy machinery',
        'Industrial floors',
        'Manufacturing equipment',
        'Oil & gas facilities',
        'Automotive workshops',
        'Marine equipment',
        'High-risk industrial environments',
        'Railways & Locomotives',
        'Manufacturing & Industrial Fabrication',
        'Power Generation & Utilities',
        'Steel & Metal Processing',
        'Robotic manufacturing',
        'Commercial kitchens',
      ],
      path: '/products/ru-ora-7',
    },
    {
      name: 'V-MUKTI 24',
      type: 'Agriculture and Environmental Management',
      tagline: 'Parthenium Eradication',
      icon: '🌿',
      color: 'from-vp-green-600 to-vp-green-500',
      accentColor: 'from-green-400 to-emerald-400',
      function: 'The ONLY Water Base Solution for the effective and safe eradication of Parthenium Hysterophorus ( Gajar Ghas).',
      keyBenefit: 'Addresses a significant national problem, promoting a healthier environment and preventing the health issues (dermatitis, asthma) and crop losses caused by this highly poisonous weed.',
      idealFor: [
        'Agricultural lands',
        'Public spaces',
        'Parks and gardens',
        'Roadsides',
        'Wasteland areas',
        'Parthenium-infested zones',
      ],
      path: '/products/v-mukti-24',
    },
  ];

  const philosophyPoints = [
    {
      title: 'Eco-Friendly',
      desc: 'All products are bio-degradable and water-based',
      icon: BsRecycle,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Non-Corrosive',
      desc: 'They do not react with metal, rubber, or PVC',
      icon: FiShield,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Safety Guaranteed',
      desc: 'Non-flammable, non-toxic, and totally harmless to human beings, plants, and the environment',
      icon: BsShieldCheck,
      gradient: 'from-amber-500 to-orange-500',
    },
  ];

  // Molecular pattern component
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-products" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <circle cx="180" cy="120" r="3" fill="currentColor" opacity="0.25" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
          <line x1="80" y1="40" x2="140" y2="70" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-products)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-vp-cream-50">
      {/* Hero Section - Page Header */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32">
        {/* Dynamic Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-vp-cream-100 via-vp-cream-200 to-white">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0, 135, 81, 0.15), transparent 50%)`,
            }}
          />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-vp-green-500 opacity-10">
          <MolecularPattern />
        </div>

        {/* Floating chemical drops */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -60, 0],
                x: [0, Math.random() * 40 - 20, 0],
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 6 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            >
              <GiChemicalDrop className="w-6 h-6 text-vp-green-500/40" />
            </motion.div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center space-x-3 bg-white/80 backdrop-blur-xl px-6 py-3 rounded-full text-vp-grey-800 mb-8 border border-vp-green-500/30"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiChemicalDrop className="text-vp-blue-600 text-2xl" />
              <span className="font-semibold">High-Performance Water Base Solutions</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-vp-grey-900 mb-6 leading-tight">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 via-vp-blue-600 to-vp-green-600">
                Sustainable Chemistry
              </span>
              <span className="block text-4xl md:text-5xl lg:text-6xl mt-4 text-vp-grey-800">
                Superior Performance
              </span>
            </h1>

            <motion.p
              className="text-xl md:text-2xl text-black mb-12 max-w-5xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Explore our range of high-performance, water-based, and biodegradable water base solution
              <br className="hidden md:block" />
              <span className="text-vp-green-600 font-semibold">designed to enhance operational efficiency and protect the environment.</span>
            </motion.p>

            {/* Industries We Serve - Enhanced with Emojis */}
            <motion.div
              className="mt-12 max-w-6xl mx-auto bg-white/60 backdrop-blur-xl rounded-3xl p-8 border border-vp-grey-200 shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-vp-green-600 to-vp-blue-600 bg-clip-text text-transparent mb-6">
                  Trusted Across Industries
                </h3>
                
                <div className="flex flex-wrap justify-center items-center gap-6 text-base md:text-lg">
                  {[
                    { emoji: '🏭', label: 'Heavy Industry' },
                    { emoji: '🌾', label: 'Agriculture' },
                    { emoji: '🛢️', label: 'Oil & Petroleum' },
                    { emoji: '⛏️', label: 'Mining' },
                    { emoji: '🚢', label: 'Marine' },
                    { emoji: '🚂', label: 'Railways' },
                    { emoji: '⚡', label: 'Power & Utilities' },
                    { emoji: '🏗️', label: 'Construction' },
                    { emoji: '🚗', label: 'Automotive' },
                    { emoji: '⚙️', label: 'Manufacturing' },
                  ].map((industry, index) => (
                    <motion.div
                      key={index}
                      className="group flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-vp-cream-100 to-white rounded-full border border-vp-grey-200 hover:border-vp-green-400 hover:shadow-md transition-all duration-300 cursor-default"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.8 + index * 0.05 }}
                      whileHover={{ scale: 1.05, y: -2 }}
                    >
                      <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
                        {industry.emoji}
                      </span>
                      <span className="font-semibold text-vp-grey-800 group-hover:text-vp-green-700 transition-colors">
                        {industry.label}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 border-2 border-white/50 rounded-full flex justify-center p-2">
            <motion.div 
              className="w-2 h-4 bg-gradient-to-b from-green-400 to-blue-400 rounded-full"
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Product Philosophy Section */}
      <section className="py-24 md:py-32 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 via-white to-blue-50/50" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-green-100 to-blue-100 rounded-full text-green-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                💡 Our Product Philosophy
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-8">
                Innovation Driven by{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600">
                  Responsibility
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-black max-w-4xl mx-auto leading-relaxed">
                At VP Chem Technologies LLP, innovation is driven by responsibility. Our products are formulated to be{' '}
                <span className="font-bold text-slate-900">effective against the toughest challenges</span> while remaining{' '}
                <span className="font-bold text-green-600">entirely safe</span>.
              </p>
            </div>

            {/* Philosophy Cards */}
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {philosophyPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  {/* Glowing background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${point.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white rounded-3xl p-8 border-2 border-slate-200 group-hover:border-green-400 transition-all duration-500 h-full shadow-lg group-hover:shadow-2xl">
                    <motion.div
                      className={`w-20 h-20 bg-gradient-to-br ${point.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <point.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 text-center">
                      {point.title}
                    </h3>
                    <p className="text-black leading-relaxed text-lg text-center">
                      {point.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/about#certifications"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-bold text-lg rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <FiAward className="text-2xl" />
                <span>View Our ISO and REACH Certifications</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Product Portfolio Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-vp-cream-100 via-white to-vp-cream-50 text-vp-grey-800 relative overflow-hidden">
        {/* Animated grid background */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(34, 197, 94, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(34, 197, 94, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Floating hexagons */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                rotate: [0, 360],
                scale: [1, 1.2, 1],
                opacity: [0.05, 0.15, 0.05],
              }}
              transition={{
                duration: 15 + i * 2,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <svg width="100" height="100" viewBox="0 0 100 100" className="text-green-400">
                <polygon points="50,10 90,30 90,70 50,90 10,70 10,30" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </motion.div>
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-20">
              <motion.span
                className="inline-block px-6 py-2 bg-vp-green-600/10 backdrop-blur-xl rounded-full text-vp-green-700 font-semibold mb-6 border border-vp-green-600/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🧪 Our Core Product Portfolio
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
                Specialized Solutions for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-vp-green-600 to-vp-blue-600">
                  Every Challenge
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-vp-grey-700 max-w-3xl mx-auto">
                Maintenance, sanitation, and environmental management
              </p>
            </div>

            {/* Product Cards */}
            <div className="space-y-16 max-w-7xl mx-auto">
              {coreProducts.map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Visual Side */}
                  <motion.div
                    className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className={`bg-gradient-to-br ${product.color} rounded-3xl p-12 relative overflow-hidden shadow-2xl`}>
                      {/* Animated background */}
                      <motion.div 
                        className="absolute inset-0 opacity-20"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                      >
                        <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-80 h-80 bg-white rounded-full blur-3xl" />
                      </motion.div>

                      {/* Chemical molecules */}
                      <div className="absolute inset-0">
                        {[...Array(8)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-3 h-3 bg-white/30 rounded-full"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                            }}
                            animate={{
                              y: [0, -30, 0],
                              opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                              duration: 3 + Math.random() * 2,
                              repeat: Infinity,
                              delay: Math.random() * 2,
                            }}
                          />
                        ))}
                      </div>

                      <div className="relative z-10 text-center">
                        <motion.div
                          animate={{ 
                            y: [0, -15, 0],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity,
                            ease: "easeInOut" 
                          }}
                          className="text-8xl mb-6"
                        >
                          {product.icon}
                        </motion.div>
                        
                        <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full mb-4">
                          <span className="text-black font-semibold text-sm">{product.type}</span>
                        </div>
                        
                        <h3 className="text-5xl md:text-6xl font-black text-black mb-2">
                          {product.name}
                        </h3>
                        <p className="text-2xl text-black font-semibold">
                          {product.tagline}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content Side */}
                  <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div>
                      <h4 className="text-2xl font-bold text-vp-green-700 mb-3 flex items-center">
                        <FiZap className="mr-2" /> Core Function
                      </h4>
                      <p className="text-lg text-vp-grey-800 leading-relaxed">
                        {product.function}
                      </p>
                    </div>

                    <div className={`bg-gradient-to-r ${product.accentColor} bg-opacity-10 border-l-4 border-vp-green-600 p-6 rounded-r-2xl`}>
                      <h4 className="text-xl font-bold text-vp-grey-800 mb-3 flex items-center">
                        <FiCheckCircle className="mr-2 text-vp-green-600" /> Key Benefit
                      </h4>
                      <p className="text-vp-grey-800 leading-relaxed">
                        {product.keyBenefit}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-xl font-bold text-vp-blue-600 mb-4 flex items-center">
                        <FiTool className="mr-2" /> Ideal For:
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        {product.idealFor.map((item, i) => (
                          <motion.div
                            key={i}
                            className="flex items-start space-x-2 bg-vp-cream-100 backdrop-blur-sm rounded-lg p-3 border border-vp-grey-200"
                            whileHover={{ backgroundColor: 'rgba(0, 135, 81, 0.05)', x: 5 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FiCheck className="text-vp-green-600 mt-1 flex-shrink-0" />
                            <span className="text-vp-grey-800 text-sm">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Link
                        to={product.path}
                        className={`inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r ${product.color} text-white font-bold text-lg rounded-2xl hover:shadow-2xl transition-all duration-300 group`}
                      >
                        <span>View Full {product.name} Details</span>
                        <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Custom Solution Section */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-vp-green-600 via-vp-blue-600 to-vp-green-600 relative overflow-hidden">
        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -80, 0],
                opacity: [0, 1, 0],
                scale: [0, 1.2, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        {/* Gradient blobs */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <span className="inline-block px-6 py-2 bg-white/20 backdrop-blur-xl rounded-full text-black font-semibold mb-8 border border-white/30">
                🔬 Need a Custom Solution?
              </span>
            </motion.div>
            
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-black mb-8 leading-tight">
              Beyond Standard Offerings
            </h2>
            
            <p className="text-xl md:text-2xl text-black mb-12 max-w-4xl mx-auto leading-relaxed">
              Our technical expertise and partnership with our manufacturer allow us to go beyond standard offerings. 
              If your industry requires a <span className="font-bold">custom formulation</span> or specific{' '}
              <span className="font-bold">technical guidance</span>, our dedicated team is ready to assist.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link
                to="/contact"
                className="group inline-flex items-center space-x-3 px-12 py-6 bg-white text-green-600 font-bold text-xl rounded-2xl hover:bg-slate-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.5 }}>
                  <GiChemicalDrop className="text-3xl" />
                </motion.div>
                <span>Consult Our Technical Team</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform text-2xl" />
              </Link>
            </motion.div>

            {/* Additional info */}
            <motion.div
              className="mt-16 grid md:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              {[
                { icon: FiZap, label: 'Fast Response', desc: 'Quick technical consultations' },
                { icon: GiFactory, label: 'Custom Formulation', desc: 'Tailored to your needs' },
                { icon: FiShield, label: 'Expert Support', desc: '20+ years of experience' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                  transition={{ duration: 0.3 }}
                >
                  <item.icon className="w-12 h-12 text-black mx-auto mb-4" />
                  <h4 className="text-black font-bold text-lg mb-2">{item.label}</h4>
                  <p className="text-black text-sm">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;



